import { type RefObject, useEffect, useRef } from 'react';

import {
  type OrientedRect,
  type Point2,
  type ReadableStore,
  minAreaRect,
} from '@frontend/viewer-core';

import { OPTIMAL_BOX_COLOR } from '../constants';
import type { DrawingModel, OverlayState } from '../session/types';
import {
  type MeasurementFormat,
  formatLength,
} from '../utils/formatMeasurement';
import {
  type ToolViewport,
  bindOverlayFrame,
  create,
  createLabelledLine,
  placeLabelledLine,
  setAttributes,
  show,
} from '../utils/toolUtils';

export type OptimalBoxOverlayDeps = {
  model: ReadableStore<DrawingModel | null>;
  hiddenLayers: ReadableStore<ReadonlySet<number>>;
  overlays: ReadableStore<OverlayState>;
  viewport: ToolViewport;
  format: MeasurementFormat;
};

/** Every point on a visible layer, for the optimal bounding box. */
const visiblePoints = (
  model: DrawingModel,
  hiddenLayers: ReadonlySet<number>,
): Point2[] => {
  const points: Point2[] = [];

  model.loops.forEach((loop) => {
    loop.chunks.forEach((chunk) => {
      if (hiddenLayers.has(chunk.layer)) {
        return;
      }
      chunk.points.forEach((point) => points.push(point));
    });
  });

  return points;
};

/**
 * Min-area rectangle around the visible geometry, labelled along two sides.
 *
 * The hull is computed when the model or hidden layers change, not once per
 * frame — recomputing it on the pan path would undo keeping rendering off React.
 */
export const useDxfOptimalBoxOverlay = ({
  model,
  hiddenLayers,
  overlays,
  viewport,
  format,
}: OptimalBoxOverlayDeps): RefObject<SVGSVGElement> => {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) {
      return;
    }

    const optimalBox = create('polygon');
    setAttributes(optimalBox, {
      fill: 'none',
      stroke: OPTIMAL_BOX_COLOR,
      'stroke-width': 1.25,
      'stroke-dasharray': '5 3',
    });
    const optimalLabels = [
      createLabelledLine(OPTIMAL_BOX_COLOR),
      createLabelledLine(OPTIMAL_BOX_COLOR),
    ];
    svg.appendChild(optimalBox);
    optimalLabels.forEach((label) => svg.appendChild(label.group));

    let obbCache: {
      model: unknown;
      hiddenLayers: unknown;
      rect: OrientedRect | null;
    } | null = null;

    const optimalRect = (): OrientedRect | null => {
      const currentModel = model.getState();
      const currentHidden = hiddenLayers.getState();
      if (
        obbCache &&
        obbCache.model === currentModel &&
        obbCache.hiddenLayers === currentHidden
      ) {
        return obbCache.rect;
      }
      const rect = currentModel
        ? minAreaRect(visiblePoints(currentModel, currentHidden))
        : null;
      obbCache = { model: currentModel, hiddenLayers: currentHidden, rect };
      return rect;
    };

    const draw = () => {
      const rect = overlays.getState().optimalBoundingBox
        ? optimalRect()
        : null;

      show(optimalBox, Boolean(rect));
      optimalLabels.forEach((label) => show(label.group, Boolean(rect)));
      if (!rect) {
        return;
      }

      const screenCorners = rect.corners.map(viewport.worldToScreen);
      optimalBox.setAttribute(
        'points',
        screenCorners.map(([x, y]) => `${x},${y}`).join(' '),
      );
      // One label per distinct side, drawn along the side it measures.
      placeLabelledLine(
        optimalLabels[0],
        screenCorners[0],
        screenCorners[1],
        formatLength(
          Math.hypot(
            rect.corners[1][0] - rect.corners[0][0],
            rect.corners[1][1] - rect.corners[0][1],
          ),
          format,
        ),
        { withTicks: false },
      );
      placeLabelledLine(
        optimalLabels[1],
        screenCorners[1],
        screenCorners[2],
        formatLength(
          Math.hypot(
            rect.corners[2][0] - rect.corners[1][0],
            rect.corners[2][1] - rect.corners[1][1],
          ),
          format,
        ),
        { withTicks: false },
      );
    };

    return bindOverlayFrame({
      draw,
      subscriptions: [
        viewport.subscribe,
        viewport.size.subscribe,
        overlays.subscribe,
        hiddenLayers.subscribe,
        model.subscribe,
      ],
      onCleanup: () => svg.replaceChildren(),
    });
  }, [model, hiddenLayers, overlays, viewport, format]);

  return svgRef;
};
